import { supabase } from '../lib/supabase';

class CalendlyService {
  constructor() {
    // Real Calendly URLs from the user
    this.baseUrl = 'https://calendly.com/comeyvivesano-info';
    this.thirtyMinCallUrl = 'https://calendly.com/comeyvivesano-info/30min';
    this.calendlyApiBase = 'https://api.calendly.com/';
    this.accessToken = import.meta.env?.VITE_CALENDLY_ACCESS_TOKEN;
  }

  // Generate Calendly URL with user data prefill
  generateCalendlyUrl(userData = {}, meetingType = '30min') {
    const baseUrl = meetingType === '30min' ? this.thirtyMinCallUrl : this.baseUrl;
    const params = new URLSearchParams();

    // Prefill user data if available
    if (userData?.name) {
      params?.append('name', userData?.name);
    }
    if (userData?.email) {
      params?.append('email', userData?.email);
    }
    if (userData?.phone) {
      params?.append('a1', userData?.phone); // Custom field for phone
    }
    
    // Add quiz result data as custom fields
    if (userData?.biologicalAge) {
      params?.append('a2', `Edad Biológica: ${userData?.biologicalAge} años`);
    }
    if (userData?.chronologicalAge) {
      params?.append('a3', `Edad Real: ${userData?.chronologicalAge} años`);
    }
    if (userData?.healthScore) {
      params?.append('a4', `Puntuación Salud: ${userData?.healthScore}/100`);
    }
    if (userData?.leadTemperature) {
      params?.append('a5', `Nivel Urgencia: ${userData?.leadTemperature}`);
    }

    return params?.toString() ? `${baseUrl}?${params?.toString()}` : baseUrl;
  }

  // Open Calendly widget with prefilled data
  openCalendlyWidget(userData = {}, meetingType = '30min') {
    const calendlyUrl = this.generateCalendlyUrl(userData, meetingType);
    
    // Track Calendly opening event
    this.trackCalendlyEvent('widget_opened', {
      meeting_type: meetingType,
      has_prefill_data: Boolean(userData?.name || userData?.email),
      biological_age: userData?.biologicalAge,
      lead_temperature: userData?.leadTemperature
    });

    // Check if Calendly widget script is available
    if (typeof window !== 'undefined' && window.Calendly) {
      window.Calendly?.initPopupWidget({
        url: calendlyUrl,
        utm: {
          utmCampaign: 'health-coaching',
          utmSource: 'edad-real-app',
          utmMedium: 'webapp',
          utmContent: meetingType
        }
      });
    } else {
      // Fallback: open in new tab
      window.open(calendlyUrl, '_blank');
    }
  }

  // Embed Calendly inline widget
  initInlineWidget(elementSelector, userData = {}, meetingType = '30min') {
    const calendlyUrl = this.generateCalendlyUrl(userData, meetingType);
    
    if (typeof window !== 'undefined' && window.Calendly) {
      window.Calendly?.initInlineWidget({
        url: calendlyUrl,
        parentElement: document.querySelector(elementSelector),
        utm: {
          utmCampaign: 'health-coaching',
          utmSource: 'edad-real-app', 
          utmMedium: 'webapp',
          utmContent: meetingType
        }
      });
    }
  }

  // Get user's availability via Calendly API
  async getAvailability(startDate, endDate) {
    if (!this.accessToken) {
      console.warn('Calendly access token not available, returning mock data');
      return this.getMockAvailability();
    }

    try {
      // First, get user information to get event types
      const userResponse = await fetch(`${this.calendlyApiBase}users/me`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!userResponse.ok) {
        throw new Error(`Failed to fetch user info: ${userResponse.status}`);
      }

      const userData = await userResponse.json();

      // Get event types for the user
      const eventTypesResponse = await fetch(`${this.calendlyApiBase}event_types?user=${encodeURIComponent(userData.resource.uri)}`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!eventTypesResponse.ok) {
        throw new Error(`Failed to fetch event types: ${eventTypesResponse.status}`);
      }

      const eventTypesData = await eventTypesResponse.json();

      // For each event type, get availability
      const allAvailability = [];

      for (const eventType of eventTypesData.collection) {
        try {
          const availabilityResponse = await fetch(
            `${this.calendlyApiBase}event_type_available_times?event_type=${encodeURIComponent(eventType.uri)}&start_time=${startDate}&end_time=${endDate}`,
            {
              headers: {
                'Authorization': `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (availabilityResponse.ok) {
            const availabilityData = await availabilityResponse.json();

            availabilityData.collection.forEach(slot => {
              const slotDate = new Date(slot.start_time);
              allAvailability.push({
                date: slotDate.toISOString().split('T')[0],
                time: slotDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                datetime: slot.start_time,
                available: true,
                event_type: eventType.name,
                event_type_uri: eventType.uri,
                duration: eventType.duration
              });
            });
          }
        } catch (error) {
          console.error(`Error fetching availability for event type ${eventType.name}:`, error);
        }
      }

      return allAvailability.sort((a, b) => new Date(a.datetime) - new Date(b.datetime));

    } catch (error) {
      console.error('Error fetching Calendly availability:', error);
      // Fallback to mock data if API fails
      return this.getMockAvailability();
    }
  }

  // Get mock availability as fallback
  getMockAvailability() {
    const mockSlots = [
      { date: '2025-01-08', time: '10:00', available: true, event_type: 'consultation' },
      { date: '2025-01-08', time: '14:00', available: true, event_type: 'consultation' },
      { date: '2025-01-08', time: '16:00', available: false, event_type: 'consultation' },
      { date: '2025-01-09', time: '09:00', available: true, event_type: 'consultation' },
      { date: '2025-01-09', time: '11:00', available: true, event_type: 'consultation' },
      { date: '2025-01-09', time: '15:00', available: true, event_type: 'consultation' },
      { date: '2025-01-10', time: '10:00', available: true, event_type: 'consultation' },
      { date: '2025-01-10', time: '13:00', available: false, event_type: 'consultation' }
    ];

    return mockSlots;
  }

  // Get scheduled events from Calendly API
  async getScheduledEvents(startDate, endDate) {
    if (!this.accessToken) {
      console.warn('Calendly access token not available, returning empty array');
      return [];
    }

    try {
      // First get user info
      const userResponse = await fetch(`${this.calendlyApiBase}users/me`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!userResponse.ok) {
        throw new Error(`Failed to fetch user info: ${userResponse.status}`);
      }

      const userData = await userResponse.json();

      // Get scheduled events with status filter to only get active events
      const eventsResponse = await fetch(
        `${this.calendlyApiBase}scheduled_events?organization=${encodeURIComponent(userData.resource.current_organization)}&min_start_time=${startDate}&max_start_time=${endDate}&status=active`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!eventsResponse.ok) {
        throw new Error(`Failed to fetch scheduled events: ${eventsResponse.status}`);
      }

      const eventsData = await eventsResponse.json();
      console.log(`📊 Calendly API returned ${eventsData.collection.length} active events`);

      // Filter events to only include future events (ignore past events)
      const now = new Date();
      const futureEvents = eventsData.collection.filter(event => {
        const eventTime = new Date(event.start_time);
        return eventTime > now;
      });

      console.log(`⏰ Filtered to ${futureEvents.length} future active events`);

      // Transform events and get invitee information
      const events = [];
      for (const event of futureEvents) {
        try {
          // Get invitee information for each event
          const inviteesResponse = await fetch(`${this.calendlyApiBase}scheduled_events/${event.uri.split('/').pop()}/invitees`, {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json'
            }
          });

          let inviteeData = null;
          if (inviteesResponse.ok) {
            const inviteesResult = await inviteesResponse.json();
            inviteeData = inviteesResult.collection[0]; // Get first invitee
          }

          // Only add events that have valid invitee data with email
          if (inviteeData && inviteeData.email) {
            events.push({
              id: event.uri.split('/').pop(),
              name: event.name,
              start_time: event.start_time,
              end_time: event.end_time,
              location: event.location,
              status: event.status,
              created_at: event.created_at,
              updated_at: event.updated_at,
              event_type: event.event_type,
              invitee: {
                name: inviteeData.name,
                email: inviteeData.email,
                phone: inviteeData.text_reminder_number,
                created_at: inviteeData.created_at,
                questions_and_answers: inviteeData.questions_and_answers
              }
            });
            console.log(`✅ Added event for ${inviteeData.email} on ${event.start_time}`);
          } else {
            console.log(`⚠️ Skipped event ${event.uri} - no valid invitee email`);
          }
        } catch (error) {
          console.error(`Error fetching invitee data for event ${event.uri}:`, error);
        }
      }

      console.log(`📋 Final result: ${events.length} valid events to sync`);
      return events;

    } catch (error) {
      console.error('Error fetching scheduled events:', error);
      return [];
    }
  }

  // Create a new booking (integrates with Supabase coaching_sessions)
  async createBooking({
    sessionDate,
    sessionType,
    title,
    description,
    userEmail,
    userName,
    userPhone = null,
    calendlyEventId = null,
    userData = {}
  }) {
    try {
      // In production, you would also call Calendly API here to create the event
      // const calendlyResponse = await this.createCalendlyEvent({...});
      
      // For now, we'll just create the database record
      const sessionData = {
        session_date: sessionDate,
        session_type: sessionType,
        title: title,
        description: description,
        status: 'scheduled',
        duration_minutes: 30, // Changed to 30 minutes for the specific URL
        notes: calendlyEventId ? `Calendly Event ID: ${calendlyEventId}` : null
      };

      // Find or create user profile
      let userId = null;
      if (userEmail) {
        const { data: existingUser } = await supabase?.from('user_profiles')?.select('id')?.eq('email', userEmail)?.single();

        if (existingUser) {
          userId = existingUser?.id;
        } else {
          const { data: newUser, error: userError } = await supabase?.from('user_profiles')?.insert({
              email: userEmail,
              full_name: userName || 'Usuario Anónimo',
              phone: userPhone,
              user_type: 'anonymous'
            })?.select('id')?.single();

          if (userError) throw userError;
          userId = newUser?.id;
        }
      }

      // Get coach ID
      const { data: coachData } = await supabase?.from('user_profiles')?.select('id')?.eq('role', 'coach')?.single();

      if (!coachData) {
        throw new Error('Coach profile not found');
      }

      // Create coaching session
      sessionData.client_id = userId;
      sessionData.coach_id = coachData?.id;

      const { data: session, error: sessionError } = await supabase?.from('coaching_sessions')?.insert(sessionData)?.select('*')?.single();

      if (sessionError) throw sessionError;

      // Track booking completion
      this.trackCalendlyEvent('booking_completed', {
        session_id: session?.id,
        session_type: sessionType,
        user_data: userData
      });

      return {
        success: true,
        session: session,
        calendlyEventId: calendlyEventId || `calendly_${Date.now()}`,
        calendlyUrl: this.generateCalendlyUrl(userData, sessionType === 'consultation' ? '30min' : 'default')
      };

    } catch (error) {
      console.error('Error creating booking:', error);
      throw new Error(`Failed to create booking: ${error?.message}`);
    }
  }

  // Cancel a booking
  async cancelBooking(sessionId, reason = null) {
    try {
      const { data, error } = await supabase?.from('coaching_sessions')?.update({ 
          status: 'cancelled', 
          notes: reason ? `Cancelled: ${reason}` : 'Cancelled by user',
          updated_at: new Date()?.toISOString()
        })?.eq('id', sessionId)?.select('*')?.single();

      if (error) throw error;

      // In production, also cancel the Calendly event
      // await this.cancelCalendlyEvent(data.calendly_event_id);

      this.trackCalendlyEvent('booking_cancelled', { session_id: sessionId, reason });

      return { success: true, session: data };

    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw new Error(`Failed to cancel booking: ${error?.message}`);
    }
  }

  // Get booking details
  async getBooking(sessionId) {
    try {
      const { data, error } = await supabase?.from('coaching_sessions')?.select(`
          *,
          client:user_profiles!coaching_sessions_client_id_fkey(
            full_name,
            email,
            phone
          ),
          coach:user_profiles!coaching_sessions_coach_id_fkey(
            full_name,
            email
          )
        `)?.eq('id', sessionId)?.single();

      if (error) throw error;
      return data;

    } catch (error) {
      console.error('Error fetching booking:', error);
      throw new Error(`Failed to fetch booking: ${error?.message}`);
    }
  }

  // Get user's bookings
  async getUserBookings(userEmail) {
    try {
      // First get user profile
      const { data: userProfile } = await supabase?.from('user_profiles')?.select('id')?.eq('email', userEmail)?.single();

      if (!userProfile) return [];

      const { data, error } = await supabase?.from('coaching_sessions')?.select(`
          *,
          coach:user_profiles!coaching_sessions_coach_id_fkey(
            full_name,
            email
          )
        `)?.eq('client_id', userProfile?.id)?.order('session_date', { ascending: true });

      if (error) throw error;
      return data || [];

    } catch (error) {
      console.error('Error fetching user bookings:', error);
      return [];
    }
  }

  // Helper method to format session data for display
  formatSessionData(session) {
    return {
      id: session?.id,
      date: new Date(session?.session_date)?.toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
      time: new Date(session?.session_date)?.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Madrid'
      }),
      duration: session?.duration_minutes || 30,
      title: session?.title,
      description: session?.description,
      status: session?.status,
      coach: session?.coach?.full_name,
      coachEmail: session?.coach?.email,
      type: session?.session_type
    };
  }

  // Analytics tracking for Calendly events
  trackCalendlyEvent(eventType, eventData = {}) {
    // Add gtag check to avoid undeclared variable error
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', `calendly_${eventType}`, {
        event_category: 'calendly',
        event_label: eventData?.session_type || 'consultation',
        ...eventData
      });
    }

    // Also track in Supabase for internal analytics
    this.logAnalyticsEvent(eventType, eventData);
  }

  async logAnalyticsEvent(eventType, eventData) {
    try {
      // This could be used for internal analytics tracking
      console.log(`Calendly Event: ${eventType}`, eventData);

      // In production, you might want to store this in an analytics table
      // await supabase.from('analytics_events').insert({
      //   event_type: eventType,
      //   event_data: eventData,
      //   created_at: new Date().toISOString()
      // });

    } catch (error) {
      console.error('Error logging analytics:', error);
    }
  }

  // Sync Calendly event with lead data using proper user_id relationships
  async sync_calendly_event_to_lead(email, calendlyEventId, eventType, startTime, customData = {}) {
    try {
      console.log('🔍 Looking for user_profile with email:', email);

      // First, find or create user_profile by email
      let { data: userProfile, error: userError } = await supabase
        .from('user_profiles')
        .select('id, email, full_name')
        .eq('email', email)
        .single();

      // If no user_profile exists, create one
      if (userError && userError.code === 'PGRST116') {
        console.log('👤 Creating new user_profile for:', email);

        const { data: newUser, error: createUserError } = await supabase
          .from('user_profiles')
          .insert({
            email: email,
            full_name: email.split('@')[0], // Use email prefix as name
            role: 'client',
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (createUserError) throw createUserError;
        userProfile = newUser;
        console.log('✅ Created user_profile:', userProfile);
      } else if (userError) {
        throw userError;
      }

      console.log('👤 Found/Created user_profile:', userProfile);

      // Now find or create newsletter_subscribers entry linked to this user
      let { data: leadData, error: leadError } = await supabase
        .from('newsletter_subscribers')
        .select('id, engagement_score, calendly_scheduled, user_id')
        .eq('user_id', userProfile.id)
        .single();

      // If no lead exists, create one linked to the user
      if (leadError && leadError.code === 'PGRST116') {
        console.log('📊 Creating new newsletter_subscriber for user_id:', userProfile.id);

        const { data: newLead, error: createLeadError } = await supabase
          .from('newsletter_subscribers')
          .insert({
            email: email,
            full_name: userProfile.full_name,
            user_id: userProfile.id,
            calendly_scheduled: true,
            calendly_event_id: calendlyEventId,
            calendly_scheduled_at: startTime,
            engagement_score: 25, // New lead gets 25 points for scheduling
            lead_source: 'calendly_direct',
            status: 'active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (createLeadError) throw createLeadError;
        leadData = newLead;
        console.log('✅ Created newsletter_subscriber:', leadData);
      } else if (leadError) {
        throw leadError;
      }

      // Calculate new engagement score (only add bonus if not already scheduled)
      const currentEngagement = leadData?.engagement_score || 0;
      const wasAlreadyScheduled = leadData?.calendly_scheduled || false;
      const schedulingBonus = wasAlreadyScheduled ? 0 : 25; // Only add bonus if first scheduling
      const newEngagementScore = Math.min(100, currentEngagement + schedulingBonus);

      console.log('📈 Engagement update:', { currentEngagement, wasAlreadyScheduled, schedulingBonus, newEngagementScore });

      // Extract custom data fields
      const { customNotes, rescheduleReason, questionsAndResponses } = customData;

      // Prepare update data with custom fields
      const updateData = {
        calendly_scheduled: true,
        calendly_event_id: calendlyEventId,
        calendly_scheduled_at: startTime,
        engagement_score: newEngagementScore,
        updated_at: new Date().toISOString()
      };

      // Add custom fields if provided
      if (customNotes) {
        updateData.calendly_custom_notes = customNotes;
      }
      if (rescheduleReason) {
        updateData.calendly_reschedule_reason = rescheduleReason;
      }
      if (questionsAndResponses) {
        updateData.calendly_questions_responses = JSON.stringify(questionsAndResponses);
      }

      console.log('📝 Update data with custom fields:', updateData);

      // Update existing lead with Calendly information and engagement score
      const { data: updatedLead, error: updateError } = await supabase
        .from('newsletter_subscribers')
        .update(updateData)
        .eq('user_id', userProfile.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Try to create or update coaching_session entry (optional step)
      const sessionTitle = `Llamada de evaluación (${startTime.split('T')[0]})`;
      const sessionDescription = `Sesión programada vía Calendly - ${eventType}`;

      console.log('📅 Attempting to create/update coaching_session...');

      try {
        // Check if coaching_session already exists for this Calendly event
        let { data: existingSession, error: sessionCheckError } = await supabase
          .from('coaching_sessions')
          .select('id')
          .eq('calendly_event_id', calendlyEventId)
          .single();

        if (sessionCheckError && sessionCheckError.code !== 'PGRST116') {
          console.warn('⚠️ Error checking existing session:', sessionCheckError);
        }

        if (!existingSession) {
          // Try to create new coaching session
          const { data: newSession, error: sessionError } = await supabase
            .from('coaching_sessions')
            .insert({
              client_id: userProfile.id,
              title: sessionTitle,
              description: sessionDescription,
              session_date: startTime,
              duration_minutes: 30,
              session_type: 'evaluation',
              status: 'scheduled',
              calendly_event_id: calendlyEventId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single();

          if (sessionError) {
            console.warn('⚠️ Could not create coaching_session (will continue without it):', sessionError.message);
            // Continue without coaching session - the lead data is already updated successfully
          } else {
            console.log('✅ Created coaching_session:', newSession);
          }
        } else {
          console.log('ℹ️ Coaching session already exists for this Calendly event');
        }
      } catch (sessionError) {
        console.warn('⚠️ Coaching session creation failed, continuing with lead-only sync:', sessionError.message);
        // This is non-critical - the important lead data has already been updated
      }

      return {
        success: true,
        lead: updatedLead,
        user: userProfile,
        isNew: !wasAlreadyScheduled,
        message: 'Lead updated with Calendly information using proper user relationships'
      };

    } catch (error) {
      console.error('Error syncing Calendly event to lead:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default new CalendlyService();